<template>
  <payment-success :payment-type="paymentType" v-if="success" />

  <div v-else-if="loading">Loading...</div>

  <div v-else-if="selectedOption?.id">
    <div class="flex items-center">
      <div
        :class="[
          'w-8 h-8 rounded-full bg-white',
          'flex items-center justify-center cursor-pointer',
        ]"
        style="box-shadow: 0px 0px 12px rgba(0, 0, 0, 0.1); margin-right: 22px"
        @click="handleGoBack"
      >
        <arrow-back color="#516672" class="w-5 h-5" />
      </div>

      <div class="won-color-green text-2xl font-semibold">
        Securely Checkout
      </div>
    </div>

    <div class="won-blue mt-2.5">Add your new payment info here.</div>

    <div
      class="grid grid-cols-1 md:grid-cols-2 gap-10 max-w-3xl mx-auto mt-6"
    >
      <div class="pr-8">
          <subscription-option :plan="selectedOption" :show-modal-to-users="showModalToUsers" selected />
      </div>

      <subscription-payment-form @payment-form:success="handleSuccess" />
    </div>
  </div>

  <div v-else>
<!--      <div v-if="!exceedLimit && !showModalToUsers" class="text-won-green-light bg-[#F8FEFA] rounded-lg text-base won-blue mt-3">-->
<!--          <p class="p-4">-->
<!--          Due to overwhelming demand, we only have 1000 spots remaining for our new tool, <span class="font-bold">AutoApplyAI!</span>  Upgrade your account and gain access to this exclusive feature.-->
<!--          </p>-->
<!--      </div>-->
      
       <div v-if="!exceedLimit && showModalToUsers && getUser().generations <= 10"  class="text-won-blue mt-6 flex flex-col justify-center">
          <h2 class="text-[24px] font-bold text-center mb-4">
            Upgrade your plan for premium features
          </h2>
          <p class="text-center font-[14px]">
            🎉 We're introducing a subscription model and launching AutoApplyAI! Don't worry, we're still offering a Forever Free Plan, ensuring a perfect fit for every need.
            <a href="https://www.wonsulting.ai/pricing" target="_blank" class="cursor-pointer underline text-blue-500">Learn more.</a>
          </p>
      </div>
      
      <div v-else-if="!exceedLimit && showModalToUsers && getUser().generations > 10"  class="text-won-blue mt-6 flex flex-col justify-center">
          <h2 class="text-[24px] font-bold text-center mb-4">
          Upgrade your plan for premium features
          </h2>
           <p class="text-center font-[14px]">🎉 We're introducing a subscription model and launching AutoApplyAI! Don't worry, we're still offering a Forever Free Plan, ensuring a perfect fit for every need. <span class="cursor-pointer text-blue-500"><a href="https://www.wonsulting.ai/pricing">Learn more.</a></span></p>
           <p class="mt-10 text-center font-[14px]">As a previous WonsultingAI customer, you’re eligible for a permanent 25% subscription discount (until cancellation), and it must be redeemed immediately as this offer is one-time only.</p>
      </div>
      
      <div v-if="exceedLimit" class="text-won-blue flex flex-col justify-center">
         <h2 class="text-[24px] font-bold text-center mb-4">
          Upgrade your plan for premium features
          </h2>
          <p class="text-center">{{ errorMessageExceededLimit }}</p>
     </div>
     
    <div class="flex justify-center items-center">
      <div
          :class="[
            'grid grid-cols-1 gap-5 mt-12 pb-10 justify-items-center',
             exceedLimit ? 'md:grid-cols-1' : 'md:grid-cols-2'
            ]"
      >
          <subscription-option 
              v-if="exceedLimit"
              v-for="plan of premiumPlans"
              :key="plan.id"
              :plan="plan"
              @option:select="handlePurchase"
              @plan:cancel="handleOpenCancelModal"
              @select:free-plan="handleSelectFreePlan"
              :show-modal-to-users="showModalToUsers"
              :allowExitToFreePlan="allowExitToFreePlan"
          />
          
         <subscription-option 
              v-else
              v-for="plan of subscriptionPlans"
              :key="plan.id"
              :plan="plan"
              @plan:resume="handleResume"
              @option:select="handlePurchase"
              @plan:cancel="handleOpenCancelModal"
              @select:free-plan="handleSelectFreePlan"
              :isSubmittingResumeSubscription="isSubmittingResumeSubscription"
              :show-modal-to-users="showModalToUsers"
              :allowExitToFreePlan="allowExitToFreePlan"
         />
      </div>
    </div>

    <CancelSubscriptionModal
      :current-plan="currentPlan"
      @cancel-subscription:success="handleCancelSubscription"
      :showModal="showCancelSubscriptionModal"
      :closeModal="hideCancelSubscriptionModal"
    />

  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { storeToRefs } from 'pinia'

import SubscriptionOption from "./purchase/subscription/subscription-option.vue";
import SubscriptionPaymentForm from './purchase/subscription/subscription-payment-form.vue'
import PaymentSuccess from './purchase/subscription/success.vue'
import CancelSubscriptionModal from './purchase/subscription/cancel-subscription-modal.vue'

import ArrowBack from '../icons/arrow-back.vue'
import { setAuthState } from "../composables/auth";
import { useSubscriptionPlanStore } from "../store/subscription.store";
import { useSubscriptionTrackerStore } from "../store/subscription-tracker.store";
import {resumeSubscription} from "../api/subscription";
import {useNotifications} from "../store/notifications.store";

const props = defineProps({
  isSettings: {
    type: Boolean,
    default: false,
  },
  paymentType: {
    type: String,
    default: 'token',
 },
 exceedLimit : {
    type: Boolean,
    default: false,
  },
 showModalToUsers : {
    type: Boolean,
    default: false,
  },
 errorMessageExceededLimit: {
    type: String,
    default: ''
 },
  allowExitToFreePlan: {
    type: Boolean,
    default: false,
  }, 
})

const emit = defineEmits(['selectedPackage', 'goback', 'selectFreePlan', 'subscribe'])

const success = ref(false)
const loading = ref(false)
const isSubmittingResumeSubscription = ref(false)
const currentPlan = ref('')
const showCancelSubscriptionModal = ref(false)
const showSuccessCancelSubscriptionModal = ref(false)
const { getUser } = setAuthState()
const subscriptionStore = useSubscriptionPlanStore()
const notificationStore = useNotifications()
const subscriptionTrackerStore = useSubscriptionTrackerStore()
const { subscriptionPlans, premiumPlans, selectedOption, isStripeLoaded } = storeToRefs(subscriptionStore)
if (!subscriptionPlans.value?.length) {
    loading.value = true
    subscriptionStore.initPlans().then(() => (loading.value = false))
}

onMounted(() => {
 if(! document.querySelector(`script[src="https://js.stripe.com/v3/"]`)) {
      let recaptchaScript = document.createElement('script')
      recaptchaScript.setAttribute('src', 'https://js.stripe.com/v3/')
      document.head.appendChild(recaptchaScript)
  } 
})

const handleGoBack = () => {
  handlePurchase(null)
  subscriptionStore.selectOption({})
  subscriptionStore.setCouponCode(null)
  emit('goback')
}

const handlePurchase = (plan) => {
    subscriptionStore.selectOption(plan)
    subscriptionTrackerStore.handleUpgradedButton()
    emit('selectedPackage')
}

const handleSuccess = async() => {
  subscriptionStore.selectOption({})
  subscriptionStore.refresh()
  loading.value = false
  success.value = true
  
  if(props.showModalToUsers) {
      emit('subscribe')
  }
  await subscriptionTrackerStore.handleSuccessfulPayment()
}

const handleCancelSubscription = async() => {
   showCancelSubscriptionModal.value = false
   showSuccessCancelSubscriptionModal.value = true
}

const hideCancelSubscriptionModal = () => {
    showCancelSubscriptionModal.value = false
}

const handleOpenCancelModal = (plan) => {
    currentPlan.value = plan?.stripe_price
    showCancelSubscriptionModal.value = true
}

const handleResume = async(price_id) => {
    isSubmittingResumeSubscription.value = true
    const result = await resumeSubscription({price_id: price_id})
    
    if (result?.data?.user) {
        setAuthState().setUser(result.data?.user)
        useSubscriptionPlanStore().setUserPlan(result?.data?.user?.current_plan)
        useSubscriptionPlanStore().setOnGracePeriod(result?.data?.user?.on_grace_period)
        notificationStore.notify('Successfully resumed your subscription')
        isSubmittingResumeSubscription.value = false
    }
}

const handleSelectFreePlan = () => {
  emit('selectFreePlan')
}
</script>
